// Microsoft Clarity — session recordings & heatmaps.
// Loaded dynamically so internal/back-office routes (admin, contabilidade,
// armazém, etc.) are never recorded: the script only loads on public routes,
// and recording is stopped/started on every route change.

const CLARITY_PROJECT_ID = "ytifpsr725";

declare global {
  interface Window {
    clarity?: ((...args: unknown[]) => void) & { q?: unknown[] };
  }
}

let loaded = false;
let recording = false;

function loadClarityScript() {
  if (loaded) return;
  loaded = true;

  window.clarity =
    window.clarity ||
    (function (...args: unknown[]) {
      (window.clarity!.q = window.clarity!.q || []).push(args);
    } as Window["clarity"]);

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`;
  const first = document.getElementsByTagName("script")[0];
  first?.parentNode?.insertBefore(script, first);
}

/**
 * Call on every route change.
 * Public route  → load (if needed) and start/resume recording.
 * Internal route → stop recording (and never load the script if the
 *                  session started there).
 */
export interface ClarityIdentity {
  userId: string;
  friendlyName: string;
  clientName?: string | null;
  companyName?: string | null;
}

let identifiedKey: string | null = null;

/**
 * Records only registered (logged-in, non-admin) customers on public routes.
 * Pass `identity = null` for anonymous visitors or admins → never recorded.
 */
export function syncClarityWithRoute(isInternal: boolean, identity: ClarityIdentity | null = null) {
  if (isInternal || !identity) {
    if (recording && window.clarity) {
      window.clarity("stop");
      recording = false;
    }
    return;
  }

  loadClarityScript();
  if (!window.clarity) return;
  if (!recording) {
    // EU consent mode: without an explicit signal Clarity runs cookieless,
    // so every visit counts as a new user and sessions fragment.
    window.clarity("consentv2", { ad_Storage: "denied", analytics_Storage: "granted" });
    window.clarity("consent", true);
    window.clarity("start");
    recording = true;
  }
  const key = `${identity.userId}|${identity.friendlyName}`;
  if (identifiedKey !== key) {
    identifiedKey = key;
    window.clarity("identify", identity.userId, undefined, undefined, identity.friendlyName);
    if (identity.clientName) window.clarity("set", "client_name", identity.clientName);
    if (identity.companyName) window.clarity("set", "company_name", identity.companyName);
  }
}
